import { useState, useEffect } from 'react';

const CONSENT_KEY = 'zl_cookie_consent';

/**
 * Lightweight cookie consent banner.
 * Stores user preference in localStorage.
 * When analytics consent is given, initializes the GTM dataLayer consent signal.
 */
const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Respect "Do Not Track" browser setting
    if (navigator.doNotTrack === '1') {
      return;
    }

    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      setVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    setVisible(false);

    // Signal GTM that consent was granted
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'cookie_consent',
      analytics_consent: 'granted',
    });
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    setVisible(false);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'cookie_consent',
      analytics_consent: 'denied',
    });
  };

  if (!visible) {
    return null;
  }

  return (
    <div style={styles.banner} role="dialog" aria-label="Cookie consent">
      <p style={styles.text}>
        We use cookies to improve your experience.{' '}
        <a href="/privacy-policy" style={styles.link}>Privacy Policy</a>
      </p>
      <div style={styles.buttons}>
        <button onClick={handleDecline} style={styles.declineBtn} type="button">
          Decline
        </button>
        <button onClick={handleAccept} style={styles.acceptBtn} type="button">
          Accept
        </button>
      </div>
    </div>
  );
};

const styles = {
  banner: {
    position: 'fixed',
    bottom: '12px',
    left: '12px',
    right: '12px',
    maxWidth: '480px',
    margin: '0 auto',
    background: 'var(--color-surface, #fff)',
    border: '1px solid var(--color-border, #e5e7eb)',
    borderRadius: '12px',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    zIndex: 9999,
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
  },
  text: {
    margin: 0,
    fontSize: '13px',
    color: 'var(--color-text, #374151)',
    flex: '1 1 auto',
    lineHeight: '1.4',
  },
  link: {
    color: 'var(--color-brand-600, #4A5A7F)',
    textDecoration: 'underline',
  },
  buttons: {
    display: 'flex',
    gap: '6px',
    flexShrink: 0,
  },
  declineBtn: {
    padding: '6px 12px',
    border: '1px solid var(--color-border, #d1d5db)',
    borderRadius: '6px',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '12px',
    color: 'var(--color-text, #374151)',
  },
  acceptBtn: {
    padding: '6px 12px',
    border: 'none',
    borderRadius: '6px',
    background: 'var(--color-brand-600)',
    color: 'var(--color-white)',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
  },
};

export default CookieConsent;
